import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { Card, CardContent, CardDivider, Flex, Icon, Text, Tooltip } from '@bitrise/bitkit';
import { BuildMetricsTimeSeries } from '@trace/types';
import { useParams } from 'react-router-dom';
import dataAtIds from '../../../dataAtIds.json';
import { trackEvent } from '../../../utils/tracking';
import Page from '../../Page/Page';
import PageBody from '../../Page/PageBody';
import PageHeader from '../../Page/PageHeader';
import PageTitle from '../../Page/PageTitle';
import TimeRange from '../../TimeRange/TimeRange';
import useBuildsSummary from '../useBuildsSummary';
import useBuildsOverTime from '../useBuildsOverTime';
import useStepsSummary from '../useStepsSummary';
import BuildTimeByStatus from '../BuildTimesByStatus/BuildTimesByStatus';
import getFormattedValue from '../../../utils/metric/getFormattedValue';
import BuildSuccessRate from '../BuildSuccessRate/BuildSuccessRate';
import BuildTimeOverTime from '../BuildTimeOverTime/BuildTimeOverTime';
import { URLStateContext } from '../../URLState/URLState';
import { AuthContextTrace } from '../../Authenticate/AuthenticateTrace';
import StepsBreakdownTable from '../StepsBreakdownTable/StepsBreakdownTable';
import PageBreadcrumb from '../../Page/PageBreadcrumb';

const BuildPerformanceOverview: FunctionComponent = () => {
  const { appId, workflowId } = useParams<{ appId: string; workflowId: string }>();
  // const match = hooks.useMediaQuery(['800px', '1200px']);
  const { dateFrom, dateTo } = useContext(URLStateContext);
  const {
    data: { defaultBranch },
  } = useContext(AuthContextTrace);
  const [[localDataOverTime, localTicks], setLocalDataOverTime] = useState<
    [BuildMetricsTimeSeries[], number[]]
  >([[], []]);

  const [[dateLeft, dateRight], setDateRange] = useState<[number | undefined, number | undefined]>([
    undefined,
    undefined,
  ]);

  const {
    data: dataSummary,
    // isError: isErrorSummary,
    // isFetching: isFetchingSummary,
    // refetch: refetchSummary,
  } = useBuildsSummary({
    filterBy: `workflow:${workflowId}`,
    start: dateLeft,
    end: dateRight,
  });

  const {
    data: dataOverTime,
    isError: isErrorOverTime,
    isFetching: isFetchingOverTime,
    refetch: refetchOverTime,
    step,
    stepSize,
    ticks,
  } = useBuildsOverTime({
    filterBy: `workflow:${workflowId}`,
    start: dateLeft,
    end: dateRight,
  });

  const {
    data: dataStepsSummary,
    isError: isErrorStepsSummary,
    isFetching: isFetchingStepsSummary,
    refetch: refetchStepsSummary,
  } = useStepsSummary({
    filterBy: `workflow:${workflowId}`,
    start: dateLeft,
    end: dateRight,
  });

  const handleSelectDateRange = (nextDateLeft: number, nextDateRight: number) => {
    setDateRange([nextDateLeft, nextDateRight]);
  };

  useEffect(() => {
    trackEvent('BuildWorkflow.Visit', {});
  }, []);

  useEffect(() => {
    setLocalDataOverTime(([localDataOverTimeState, localTicksState]) => [
      !localDataOverTimeState.length && dataOverTime.length ? dataOverTime : localDataOverTimeState,
      !localTicksState.length && ticks.length ? ticks : localTicksState,
    ]);
  }, [dataOverTime, ticks]);

  useEffect(() => {
    setDateRange([undefined, undefined]);
    setLocalDataOverTime([[], []]);
  }, [dateFrom, dateTo]);

  return (
    <Page data-trace-at-id={dataAtIds.BuildPerformanceOverviewPage}>
      <PageHeader>
        <PageBreadcrumb to={`/${appId}/buildPerformance`}>Build Performance</PageBreadcrumb>
        <PageTitle>
          <Flex alignChildrenVertical="middle" direction="horizontal" gap="x2">
            <Flex>{workflowId} workflow</Flex>

            <Flex>
              <Tooltip
                maxWidth="16rem"
                title="An overview of your app’s performance in the
                    critical areas of Stability, Responsiveness and Consumption,
                    broken down by a variety of device attributes and demographics
                    for a selected time period."
              >
                {({ ref, ...rest }) => (
                  <Icon {...rest} innerRef={ref} name="Info" textColor="gray-5" />
                )}
              </Tooltip>
            </Flex>
          </Flex>
        </PageTitle>

        <Flex alignChildrenHorizontal="between" direction="horizontal" gap="x4" margin="x5">
          <Flex grow />

          <Flex>
            <TimeRange />
          </Flex>
        </Flex>
      </PageHeader>

      <PageBody>
        <Card direction="vertical" elevation="x2" borderRadius="x4">
          <CardContent>
            <BuildTimeByStatus
              dateLeft={dateLeft}
              dateRight={dateRight}
              dataOverTime={localDataOverTime}
              isError={isErrorOverTime}
              isFetching={isFetchingOverTime}
              onDateRangeChange={handleSelectDateRange}
              refetch={refetchOverTime}
              step={step}
              stepSize={stepSize}
              ticks={localTicks}
              withBackground
              withTimeSelector
            />
          </CardContent>

          <CardContent paddingHorizontal="x16" paddingVertical="x12">
            <Text align="middle" margin="x12">
              <Flex alignChildrenHorizontal="around" direction="horizontal">
                <Flex>
                  <Text config="4">{getFormattedValue('1', dataSummary[0]?.totalBuilds)}</Text>
                  <Text>Number of builds</Text>
                </Flex>

                <Flex>
                  <Text config="4">
                    {getFormattedValue(
                      'seconds',
                      dataSummary[0]?.totalTimeSuccess / dataSummary[0]?.totalBuildsSuccess,
                    )}
                  </Text>
                  <Text>Average successful build time</Text>
                </Flex>

                <Flex>
                  <Text config="4">{getFormattedValue('seconds', dataSummary[0]?.totalTime)}</Text>
                  <Text>Total build minutes</Text>
                </Flex>
              </Flex>
            </Text>

            <Flex alignChildrenVertical="end" direction="horizontal" gap="x16">
              <Flex>
                <BuildSuccessRate
                  radius={90}
                  totalBuilds={dataSummary[0]?.totalBuilds}
                  totalBuildsSuccess={dataSummary[0]?.totalBuildsSuccess}
                />

                <Text align="middle" margin="x2">
                  Build success rate
                </Text>
              </Flex>

              <Flex grow initial="none">
                <BuildTimeOverTime
                  branch={defaultBranch}
                  dataOverTime={dataOverTime}
                  isError={isErrorOverTime}
                  isFetching={isFetchingOverTime}
                  refetch={refetchOverTime}
                  step={step}
                  stepSize={stepSize}
                  ticks={ticks}
                />

                <Text align="middle" margin="x6">
                  Successful build time
                </Text>
              </Flex>
            </Flex>
          </CardContent>

          <CardDivider />

          <CardContent paddingHorizontal="x16" paddingVertical="x12">
            <Flex direction="vertical" gap="x16">
              <Flex>
                <Text config="5" margin="x8">
                  Longest running steps
                </Text>

                <StepsBreakdownTable
                  dataSummary={dataStepsSummary}
                  isFetching={isFetchingStepsSummary}
                  isError={isErrorStepsSummary}
                  metric="avgBuildTime"
                  refetch={refetchStepsSummary}
                />
              </Flex>

              <Flex>
                <Text config="5" margin="x8">
                  Least reliable steps
                </Text>

                <StepsBreakdownTable
                  dataSummary={dataStepsSummary}
                  isFetching={isFetchingStepsSummary}
                  isError={isErrorStepsSummary}
                  metric="successRate"
                  refetch={refetchStepsSummary}
                />
              </Flex>
            </Flex>
          </CardContent>
        </Card>
      </PageBody>
    </Page>
  );
};

export default BuildPerformanceOverview;
